/** The battle console's running commentary, built from the kernel's own events.
 *
 * The classic's console is a narrator, not a status line: it reads out every blow as
 * it lands, centred in the recess, and it is the single most characteristic
 * thing on the battle screen.
 *
 * ## Which sentences are transcribed, and which are only in the voice
 *
 * Two of the sentences below were said to be transcribed letter for letter off
 * A reference screenshot:
 *
 *     The Ballista does 4 damage.
 *     Next round begins.
 *
 * **That file does not exist** - `find /` returns nothing for it and the only
 * `refs/` directory on this machine is the critic's own reference set - so
 * neither string is a transcription of anything, and "Next round begins."
 * remains *remembered, not checkable*: nothing on disk carries it.
 *
 * THREE real the classic console lines are on disk, in the critic's reference set,
 * and between them they settle most of this file's conventions:
 *
 *   `CLASSIC_Battlefield.jpg` (647x486, a downscale of a native 800x600 the classic
 *   frame), one centred line in the recess:
 *
 *       Ulysa casts 'Sinking Sand'.
 *
 *   a reference screenshot (710x533, the classic gallery for *the classic: Complete*), BOTH
 *   lines of the recess in use at once:
 *
 *       The Nightfeeders do 4 damage.
 *       The Skeletons do 20 damage.
 *
 *   a reference screenshot (same source), a hover hint rather than a log line:
 *
 *       Fly High Radiants here.
 *
 * All three were read at 3x-5x on 2026-08-30 and are unambiguous at that
 * magnification; the wording below is quoted from the pixels, not recalled.
 *
 * What they establish:
 *
 *   - the voice: present tense, definite subject, sentence case, full stop,
 *     centred, no player name and no "you";
 *   - the damage sentence, letter for letter: `The <Plural> do <N> damage.`
 *     That is exactly what `BattleAttacked` builds below, so the shape of
 *     "The Ballista does 4 damage." is now anchored even though that
 *     particular instance is not;
 *   - a spell line names the *caster*, which is rule 1 below;
 *   - **the classic single-quotes the spell's name.** `${who} casts ${spell}.` did
 *     not, and that was a live wording bug; it is fixed, and the same quoting
 *     is applied to the reflection line for consistency (the reflection line's
 *     own wording is still ours - see below).
 *
 * Everything else here - the morale and luck lines, the summon line, the wall
 * lines, the endings, the "and kills N" clause, and the no-commander form of
 * the cast line - is written in that voice and is NOT a transcription. Nothing
 * on disk can confirm any of them. Where one differs from the classic's own string it
 * is a wording bug, not a rules bug, and can be corrected here alone.
 *
 * Three shape decisions are load-bearing:
 *
 * 1. **The original names the stack, never the player.** "The Ballista does 4
 *    damage.", not "Aldwin's Ballista" and not "You do 4 damage." The one
 *    exception is a spell, which is cast by a person: "Aldwin casts
 *    'Bless'.", which is the form `CLASSIC_Battlefield.jpg` shows.
 * 2. **Nothing here decides a rule.** Every number in every sentence is read
 *    off a `GameEvent` the kernel emitted. This file cannot disagree with the
 *    simulation because it never computes anything the simulation computed.
 * 3. **It is pure.** `@game/render` never sees a `GameEvent` (I10) and nothing
 *    in this file touches React or Pixi, so `apps/web/test/battle-narration.spec.ts`
 *    can assert the sentences directly.
 *
 * The one approximation, stated because it is visible: the `BattleState` handed
 * in is the state AFTER the batch - `executeBattleCommand` mutates in place - so
 * a stack's count is read after the blow, not before it. That only ever decides
 * singular against plural in the *attacker's* name, never a number, and the
 * "kills N Skeletons" clause takes its plurality from the event's own `killed`.
 */

import type { BattleState, ContentRegistry, GameEvent } from "@game/core";

/** What the console knows about the two commanders; see `UiState.commanders`. */
export interface CommanderNames {
  readonly commanders: readonly [string | null, string | null];
}

/**
 * How many lines the recess holds.
 *
 * Two, and this one IS measured: a reference screenshot shows the console recess with
 * two centred lines in it at once ("The Nightfeeders do 4 damage." above "The
 * Skeletons do 20 damage.") and no room for a third. `CLASSIC_Battlefield.jpg`
 * shows a single line sitting at the TOP of the same recess with the lower half
 * empty, which is the one-line case of the same two-line box.
 */
export const NARRATION_LINES = 2;

/**
 * A stack, the way the console says its name: "The Ballista", "The Master Bowyers".
 *
 * Count 0 - a stack wiped out inside this same batch - reads as plural, which
 * is right far more often than it is wrong: a stack of one is usually a war
 * machine, and a war machine appears here as an attacker while it still exists.
 */
function stackName(state: BattleState, content: ContentRegistry, slot: number): string {
  const st = state.stacks;
  if (slot < 0 || slot >= st.n) return "The stack";
  const def = content.creatures.get(st.def[slot]!);
  if (def === undefined) return "The stack";
  return `The ${st.count[slot] === 1 ? def.name : def.namePlural}`;
}

/** Singular or plural agreement for the verb after `stackName`. */
function isPlural(state: BattleState, slot: number): boolean {
  const st = state.stacks;
  if (slot < 0 || slot >= st.n) return false;
  return st.count[slot] !== 1;
}

/** "3 Skeletons", "1 Skeleton" - the creature named by however many died. */
function killedPhrase(
  state: BattleState, content: ContentRegistry, slot: number, killed: number,
): string {
  const st = state.stacks;
  const id = slot >= 0 && slot < st.n ? st.def[slot] : undefined;
  const def = id === undefined ? undefined : content.creatures.get(id);
  if (def === undefined) return `${String(killed)} creatures`;
  return `${String(killed)} ${killed === 1 ? def.name : def.namePlural}`;
}

/**
 * One batch of kernel events as sentences, in the order they happened.
 *
 * Movement and the opening of a stack's turn are deliberately silent: the classic
 * narrates neither, and a line per step would push every blow off the strip
 * before it could be read.
 */
/**
 * What each racial reads as on the strip. Wording is ours - The classic has no
 * racials and so no line to transcribe - but it follows the file's own
 * conventions: a sentence, the commander as its subject, and no spell quoting
 * because a racial is not a spell.
 */
const RACIAL_LINE: Readonly<Record<string, string>> = Object.freeze({
  gate: "tears a gate open",
  rage: "whips the line into a rage",
  ritual: "spends blood for power",
  train: "raises a company to its colours",
  swiftness: "calls the line to speed",
  channel: "draws the mana back",
  raise: "calls the fallen up",
  plague: "breathes a plague over the field",
  attune: "attunes the line against magic",
  press: "presses fresh ammunition on the archers",
  overhaul: "orders the engines overhauled",
  runegift: "cuts runes for the whole line",
  flood: "calls the water in",
});

export function narrateBattleEvents(
  state: BattleState,
  content: ContentRegistry,
  events: readonly GameEvent[],
  names: CommanderNames,
): readonly string[] {
  const out: string[] = [];
  for (const e of events) {
    switch (e.type) {
      case "BattleRoundStarted": {
        // Round 1 does not "begin again"; the battle starting is its own line.
        if (e.round > 1) out.push("Next round begins.");
        break;
      }
      case "BattleAttacked": {
        const dmg = String(e.damage);
        if (e.attacker === e.target) {
          // The moat and the tower report themselves this way: nothing charged,
          // so the kernel names the victim as its own attacker.
          const who = stackName(state, content, e.target);
          const many = isPlural(state, e.target);
          out.push(e.killed > 0
            ? `${who} ${many ? "take" : "takes"} ${dmg} damage and `
              + `${many ? "lose" : "loses"} ${killedPhrase(state, content, e.target, e.killed)}.`
            : `${who} ${many ? "take" : "takes"} ${dmg} damage.`);
          break;
        }
        const who = stackName(state, content, e.attacker);
        const many = isPlural(state, e.attacker);
        out.push(e.killed > 0
          ? `${who} ${many ? "do" : "does"} ${dmg} damage and `
            + `${many ? "kill" : "kills"} ${killedPhrase(state, content, e.target, e.killed)}.`
          : `${who} ${many ? "do" : "does"} ${dmg} damage.`);
        break;
      }
      case "BattleMorale": {
        const who = stackName(state, content, e.stack);
        out.push(e.good
          ? `High morale enables ${who} to attack again.`
          : `Low morale causes ${who} to freeze in panic.`);
        break;
      }
      case "BattleLuck": {
        // Two words, because the blow it modifies is the very next line and the
        // recess holds only two: a sentence here would push the damage out.
        out.push(e.good ? "Lucky strike!" : "Unlucky strike!");
        break;
      }
      case "BattleSpellCast": {
        const spell = content.spells.get(e.spell)?.name ?? (e.spell as string);
        const who = names.commanders[e.side];
        // The quotes are the classic's: "Ulysa casts 'Sinking Sand'." on
        // `CLASSIC_Battlefield.jpg`. The no-commander form is ours - a spell with
        // no person behind it has no reference on disk - but it keeps the
        // quoting so the two cannot read as two different games.
        out.push(who === null ? `'${spell}' is cast.` : `${who} casts '${spell}'.`);
        break;
      }
      case "BattleSpellReflected": {
        const spell = content.spells.get(e.spell)?.name ?? (e.spell as string);
        // Wording ours, quoting the classic's: nothing on disk shows a reflection
        // line, but a spell name is single-quoted wherever the original prints
        // one, so it is quoted here too.
        out.push(`${stackName(state, content, e.from)} reflect${isPlural(state, e.from) ? "" : "s"}`
          + ` '${spell}' onto ${stackName(state, content, e.to).replace(/^The /, "the ")}.`);
        break;
      }
      case "BattleStackSummoned": {
        const def = content.creatures.get(e.creature);
        const name = def === undefined ? "creatures"
          : e.count === 1 ? def.name : def.namePlural;
        out.push(`${String(e.count)} ${name} ${e.count === 1 ? "joins" : "join"} the battle.`);
        break;
      }
      /**
       * The hero's own blow. Named after the commander rather than after a
       * stack, because that is the whole point of the line: the console has
       * spent the entire game reporting what creatures did to each other, and
       * this is the one sentence where the man on the hill is the subject.
       *
       * Wording ours - nothing on disk shows a hero striking, because the classic has
       * no such action - but it follows the transcribed convention exactly:
       * "<subject> does N damage.", and the kill clause of `BattleAttacked`.
       */
      case "BattleHeroStruck": {
        const who = names.commanders[e.side] ?? "The commander";
        const dmg = String(e.damage);
        out.push(e.killed > 0
          ? `${who} strikes for ${dmg} damage and kills `
            + `${killedPhrase(state, content, e.target, e.killed)}.`
          : `${who} strikes for ${dmg} damage.`);
        break;
      }
      /**
       * The racial, named by what it IS.
       *
       * One line per kind rather than one line for all thirteen: "calls on the
       * old rites" is what a placeholder says, and a player who has just spent
       * the only charge in the battle should be told what they spent it on.
       * Keyed on the kernel's own kind string, so a faction added in content
       * gets the fallback rather than the wrong sentence.
       */
      case "BattleRacialUsed": {
        const who = names.commanders[e.side] ?? "The commander";
        out.push(`${who} ${RACIAL_LINE[e.kind] ?? "calls on the old rites"}.`);
        break;
      }
      case "BattleAbilityActivated": {
        const who = stackName(state, content, e.stack);
        const many = isPlural(state, e.stack);
        // One sentence per effect rather than a generic one: "uses its special
        // ability" is what a tooltip says, not what a narrator says.
        const verb = e.effect === "fortify" ? (many ? "dig in" : "digs in")
          : e.effect === "warcry" ? (many ? "raise a war cry" : "raises a war cry")
            : e.effect === "terrify" ? (many ? "let out a wail" : "lets out a wail")
              : (many ? "spray venom" : "sprays venom");
        out.push(`${who} ${verb}.`);
        break;
      }
      case "BattleWallHit": {
        out.push(e.integrity <= 0
          ? "A section of the wall crumbles."
          : "A section of the wall is damaged.");
        break;
      }
      case "BattleEnded": {
        out.push(endingLine(e.kind, e.winner));
        break;
      }
      // Movement, a stack taking its turn, and a stack's death are silent: the
      // original narrates none of them, and the kill is already in the damage
      // sentence that caused it.
      default: break;
    }
  }
  return out;
}

function endingLine(kind: string, winner: 0 | 1 | null): string {
  if (kind === "retreat") return "The battle is over - the enemy has fled the field.";
  if (kind === "surrender") return "The battle is over - terms of surrender are accepted.";
  if (winner === null) return "The battle is over - neither army holds the field.";
  return "The battle is over.";
}

/**
 * The last `NARRATION_LINES` of a running commentary, oldest line first.
 *
 * A line identical to the one before it is dropped. Two stacks moving inside
 * one batch can push two "Next round begins." through in a row, and the recess
 * then reads the same sentence twice, centred, one above the other - which
 * looks like a repaint bug rather than like a log. Observed on a real battle,
 * not hypothesised.
 */
export function narrationWindow(
  previous: readonly string[], added: readonly string[],
): readonly string[] {
  if (added.length === 0) return previous;
  const all = [...previous];
  for (const line of added) {
    if (all[all.length - 1] === line) continue;
    all.push(line);
  }
  return all.slice(Math.max(0, all.length - NARRATION_LINES));
}
